import type {
  AuditorControlFailureRow,
  AuditorEntityRiskRow,
} from "../../../api/frameworksApi";

export interface OrgRiskRow extends Omit<AuditorEntityRiskRow, "priority"> {
  priority: AttentionPriority;
  repeatedFailureControls: RepeatedControlFailure[];
}

export type ControlFailureRow = AuditorControlFailureRow;

export interface FailureReasonRow {
  reason: string;
  count: number;
  pct: number;
}

export interface PassFailTrendPoint {
  period: string;
  pctPass: number;
}

export interface PassFailSummary {
  totalChecks: number;
  passed: number;
  failed: number;
  pctPass: number;
  trend: PassFailTrendPoint[];
}

export type PriorityDriverKey =
  | "complianceGap"
  | "criticalFindings"
  | "failedControlDensity"
  | "overdueRemediation";

export const PRIORITY_DRIVERS: Array<{
  key: PriorityDriverKey;
  label: string;
  shortLabel: string;
  headerLines: [string, string];
  hex: string;
  weight: number;
  description: string;
}> = [
  {
    key: "complianceGap",
    label: "Compliance Gap",
    shortLabel: "Compliance",
    headerLines: ["Compliance", "Gap"],
    hex: "#ef4444",
    weight: 0.3333,
    description: "100 minus the compliance score",
  },
  {
    key: "criticalFindings",
    label: "Unmanaged Critical Findings",
    shortLabel: "Unmanaged Critical",
    headerLines: ["Unmanaged", "Critical"],
    hex: "#f97316",
    weight: 0.2778,
    description: "Open critical findings with no active remediation plan",
  },
  {
    key: "failedControlDensity",
    label: "Failed Control Density",
    shortLabel: "Failed Control",
    headerLines: ["Failed", "Density"],
    hex: "#eab308",
    weight: 0.2222,
    description: "Failed controls as a share of applicable controls",
  },
  {
    key: "overdueRemediation",
    label: "Overdue Remediation",
    shortLabel: "Overdue",
    headerLines: ["Overdue", "Remediation"],
    hex: "#3b82f6",
    weight: 0.1667,
    description: "Remediation items past their scheduled completion date",
  },
];

export const ATTENTION_THRESHOLD = 40;
export const LOW_COVERAGE_THRESHOLD = 60;

export interface PriorityDriver {
  key: PriorityDriverKey;
  label: string;
  hex: string;
  weight: number;
  raw: string;
  normalized: number;
  contribution: number;
}

export interface AttentionPriority {
  score: number;
  drivers: PriorityDriver[];
  coveragePct: number | null;
  lowCoverage: boolean;
  mustAct: boolean;
  needsAttention: boolean;
  explanation: string;
}

export interface RepeatedControlFailure {
  controlId: string;
  title: string;
  severity: ControlFailureRow["severity"];
  consecutiveCycles: number;
  since: string;
}

export const REPEAT_STREAK_BUCKETS: Array<{
  key: "twoCycles" | "threeCycles" | "fourPlusCycles";
  label: string;
  hex: string;
  min: number;
  max: number;
}> = [
  { key: "twoCycles", label: "2 cycles", hex: "#fbbf24", min: 2, max: 2 },
  { key: "threeCycles", label: "3 cycles", hex: "#f97316", min: 3, max: 3 },
  { key: "fourPlusCycles", label: "4+ cycles", hex: "#b91c1c", min: 4, max: Infinity },
];

export function repeatStreakBucket(cycles: number) {
  return REPEAT_STREAK_BUCKETS.find((bucket) => cycles >= bucket.min && cycles <= bucket.max) ?? REPEAT_STREAK_BUCKETS[0];
}

function toPriority(row: AuditorEntityRiskRow): AttentionPriority {
  const byKey = new Map((row.priority?.drivers ?? []).map((driver) => [driver.key, driver]));
  const drivers: PriorityDriver[] = PRIORITY_DRIVERS.flatMap((meta) => {
    const driver = byKey.get(meta.key);
    if (!driver) return [];
    return [{
      key: meta.key,
      label: meta.label,
      hex: meta.hex,
      weight: driver.weight,
      raw: driver.raw,
      normalized: driver.normalized,
      contribution: driver.contribution,
    }];
  });
  return {
    score: row.priority?.score ?? 0,
    drivers,
    coveragePct: row.priority?.coveragePct ?? null,
    lowCoverage: row.priority?.lowCoverage ?? false,
    mustAct: row.priority?.mustAct ?? false,
    needsAttention: row.priority?.needsAttention ?? false,
    explanation: row.priority?.explanation ?? "",
  };
}

export function toOrgRiskRow(row: AuditorEntityRiskRow): OrgRiskRow {
  return { ...row, priority: toPriority(row), repeatedFailureControls: [] };
}

export const NOT_MEASURED = "Not measured";
export const UNMEASURED_HEX = "#64748b";

export function complianceHex(score: number | null): string {
  if (score == null) return UNMEASURED_HEX;
  if (score >= 80) return "#10b981";
  if (score >= 60) return "#f59e0b";
  if (score >= 40) return "#f97316";
  return "#ef4444";
}

export function failedControlsTrendPct(row: OrgRiskRow): number | null {
  const prev = row.failedControlsPrevQuarter;
  if (prev == null) return null;
  if (prev <= 0) return row.failedControls > 0 ? 100 : 0;
  return Math.round(((row.failedControls - prev) / prev) * 100);
}

export function riskLevel(score: number): { label: string; hex: string } {
  if (score >= 75) return { label: "Critical", hex: "#ef4444" };
  if (score >= 60) return { label: "High", hex: "#f97316" };
  if (score >= ATTENTION_THRESHOLD) return { label: "Medium", hex: "#f59e0b" };
  return { label: "Low", hex: "#64748b" };
}

export function severityHex(severity: ControlFailureRow["severity"]): string {
  const map: Record<ControlFailureRow["severity"], string> = {
    Critical: "#ef4444",
    High: "#f97316",
    Medium: "#f59e0b",
    Low: "#0ea5e9",
    Unrated: UNMEASURED_HEX,
  };
  const key = String(severity ?? "").trim().toLowerCase();
  const hit = (Object.keys(map) as ControlFailureRow["severity"][]).find(
    (value) => value.toLowerCase() === key,
  );
  return hit ? map[hit] : UNMEASURED_HEX;
}

export const CONTRIBUTOR_COLORS = ["#ef4444", "#f97316", "#f59e0b", "#8b5cf6", "#4f8ef7", "#22d3ee"];
